import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const TravelPreferencesPage = () => {
  const [preferences, setPreferences] = useState({
    seatPreference: 'window',
    mealPreference: 'standard',
    specialAssistance: [],
    frequentFlyerNumbers: [],
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    travelDocuments: {
      passportNumber: '',
      passportExpiry: '',
      nationality: '',
      knownTravelerNumber: '',
      globalEntryNumber: ''
    },
    notifications: {
      flightUpdates: true,
      promotions: false,
      checkInReminders: true,
      boardingPass: true
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddFFN, setShowAddFFN] = useState(false);
  const [newFFN, setNewFFN] = useState({ airline: '', number: '' });

  useEffect(() => {
    // Simulate API call to fetch preferences
    setTimeout(() => {
      setPreferences(prev => ({
        ...prev,
        frequentFlyerNumbers: [
          { id: 1, airline: 'JetScanner Airways', number: 'JS123456789' },
          { id: 2, airline: 'Sky Connect', number: 'SC987654321' }
        ],
        emergencyContact: {
          name: 'Jane Doe',
          relationship: 'Spouse',
          phone: '+1-555-0124',
          email: 'jane.doe@email.com'
        },
        travelDocuments: {
          passportNumber: 'A12345678',
          passportExpiry: '2028-05-15',
          nationality: 'US',
          knownTravelerNumber: 'KTN123456',
          globalEntryNumber: 'GE7890123'
        }
      }));
      setLoading(false);
    }, 1000);
  }, []);

  const handlePreferenceChange = (category, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleSpecialAssistanceChange = (assistance) => {
    setPreferences(prev => ({
      ...prev,
      specialAssistance: prev.specialAssistance.includes(assistance)
        ? prev.specialAssistance.filter(item => item !== assistance)
        : [...prev.specialAssistance, assistance]
    }));
  };

  const handleAddFFN = () => {
    if (newFFN.airline && newFFN.number) {
      setPreferences(prev => ({
        ...prev,
        frequentFlyerNumbers: [
          ...prev.frequentFlyerNumbers,
          { id: Date.now(), ...newFFN }
        ]
      }));
      setNewFFN({ airline: '', number: '' });
      setShowAddFFN(false);
    }
  };

  const handleRemoveFFN = (id) => {
    setPreferences(prev => ({
      ...prev,
      frequentFlyerNumbers: prev.frequentFlyerNumbers.filter(ffn => ffn.id !== id)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      toast.success('Travel preferences saved successfully!');
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
          <p className="text-jet-gray-600">Loading travel preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-jet-gray-900 mb-2">Travel Preferences</h1>
        <p className="text-jet-gray-600">Customize your travel experience and save time on future bookings</p>
      </div>

      <div className="space-y-6">
        {/* Flight Preferences */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Flight Preferences</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Seat Preference
              </label>
              <select
                value={preferences.seatPreference}
                onChange={(e) => setPreferences(prev => ({ ...prev, seatPreference: e.target.value }))}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
              >
                <option value="window">Window Seat</option>
                <option value="aisle">Aisle Seat</option>
                <option value="middle">Middle Seat</option>
                <option value="no-preference">No Preference</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Meal Preference
              </label>
              <select
                value={preferences.mealPreference}
                onChange={(e) => setPreferences(prev => ({ ...prev, mealPreference: e.target.value }))}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
              >
                <option value="standard">Standard Meal</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="vegan">Vegan</option>
                <option value="kosher">Kosher</option>
                <option value="halal">Halal</option>
                <option value="gluten-free">Gluten-Free</option>
                <option value="diabetic">Diabetic</option>
                <option value="no-meal">No Meal</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-jet-gray-700 mb-3">
              Special Assistance
            </label>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                'Wheelchair assistance',
                'Mobility assistance',
                'Visual impairment assistance',
                'Hearing impairment assistance',
                'Traveling with infant',
                'Unaccompanied minor',
                'Pet in cabin',
                'Extra baggage assistance'
              ].map(assistance => (
                <label key={assistance} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={preferences.specialAssistance.includes(assistance)}
                    onChange={() => handleSpecialAssistanceChange(assistance)}
                    className="mr-2 h-4 w-4 text-jet-yellow focus:ring-jet-yellow border-jet-gray-300 rounded"
                  />
                  <span className="text-sm text-jet-gray-700">{assistance}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Frequent Flyer Numbers */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-jet-gray-900">Frequent Flyer Numbers</h3>
            <button
              onClick={() => setShowAddFFN(true)}
              className="bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium px-4 py-2 rounded-md transition-colors text-sm"
            >
              Add Number
            </button>
          </div>

          {preferences.frequentFlyerNumbers.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">✈️</div>
              <p className="text-jet-gray-500">No frequent flyer numbers added yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {preferences.frequentFlyerNumbers.map(ffn => (
                <div key={ffn.id} className="flex items-center justify-between p-3 border border-jet-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-jet-gray-900">{ffn.airline}</div>
                    <div className="text-sm text-jet-gray-600">{ffn.number}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveFFN(ffn.id)}
                    className="text-jet-red-600 hover:text-jet-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add FFN Modal */}
          {showAddFFN && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-jet-gray-900">Add Frequent Flyer Number</h4>
                  <button
                    onClick={() => setShowAddFFN(false)}
                    className="text-jet-gray-400 hover:text-jet-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                      Airline
                    </label>
                    <input
                      type="text"
                      value={newFFN.airline}
                      onChange={(e) => setNewFFN(prev => ({ ...prev, airline: e.target.value }))}
                      className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                      placeholder="e.g., JetScanner Airways"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                      Frequent Flyer Number
                    </label>
                    <input
                      type="text"
                      value={newFFN.number}
                      onChange={(e) => setNewFFN(prev => ({ ...prev, number: e.target.value }))}
                      className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                      placeholder="Enter your frequent flyer number"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => setShowAddFFN(false)}
                      className="flex-1 border border-jet-gray-300 hover:border-jet-gray-400 text-jet-gray-700 font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddFFN}
                      className="flex-1 bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900 font-medium py-2 px-4 rounded-md transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Emergency Contact */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Emergency Contact</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={preferences.emergencyContact.name}
                onChange={(e) => handlePreferenceChange('emergencyContact', 'name', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Relationship
              </label>
              <select
                value={preferences.emergencyContact.relationship}
                onChange={(e) => handlePreferenceChange('emergencyContact', 'relationship', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
              >
                <option value="">Select relationship</option>
                <option value="Spouse">Spouse</option>
                <option value="Parent">Parent</option>
                <option value="Child">Child</option>
                <option value="Sibling">Sibling</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={preferences.emergencyContact.phone}
                onChange={(e) => handlePreferenceChange('emergencyContact', 'phone', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={preferences.emergencyContact.email}
                onChange={(e) => handlePreferenceChange('emergencyContact', 'email', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter email address"
              />
            </div>
          </div>
        </div>

        {/* Travel Documents */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Travel Documents</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Passport Number
              </label>
              <input
                type="text"
                value={preferences.travelDocuments.passportNumber}
                onChange={(e) => handlePreferenceChange('travelDocuments', 'passportNumber', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter passport number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Passport Expiry Date
              </label>
              <input
                type="date"
                value={preferences.travelDocuments.passportExpiry}
                onChange={(e) => handlePreferenceChange('travelDocuments', 'passportExpiry', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Nationality
              </label>
              <select
                value={preferences.travelDocuments.nationality}
                onChange={(e) => handlePreferenceChange('travelDocuments', 'nationality', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
              >
                <option value="">Select nationality</option>
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="DE">Germany</option>
                <option value="FR">France</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Known Traveler Number (TSA PreCheck)
              </label>
              <input
                type="text"
                value={preferences.travelDocuments.knownTravelerNumber}
                onChange={(e) => handlePreferenceChange('travelDocuments', 'knownTravelerNumber', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter KTN number"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-jet-gray-700 mb-2">
                Global Entry Number
              </label>
              <input
                type="text"
                value={preferences.travelDocuments.globalEntryNumber}
                onChange={(e) => handlePreferenceChange('travelDocuments', 'globalEntryNumber', e.target.value)}
                className="w-full px-3 py-2 border border-jet-gray-300 rounded-md focus:outline-none focus:border-jet-yellow"
                placeholder="Enter Global Entry number"
              />
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-jet-gray-900 mb-4">Notification Preferences</h3>
          
          <div className="space-y-4">
            {[
              { key: 'flightUpdates', label: 'Flight Updates', description: 'Gate changes, delays, and cancellations' },
              { key: 'checkInReminders', label: 'Check-in Reminders', description: 'Reminders when check-in opens' },
              { key: 'boardingPass', label: 'Boarding Pass', description: 'Mobile boarding pass delivery' },
              { key: 'promotions', label: 'Promotions & Offers', description: 'Special deals and discounts' }
            ].map(notification => (
              <div key={notification.key} className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-jet-gray-900">{notification.label}</div>
                  <div className="text-sm text-jet-gray-600">{notification.description}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={preferences.notifications[notification.key]}
                    onChange={(e) => handlePreferenceChange('notifications', notification.key, e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-jet-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jet-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-jet-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-jet-yellow"></div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-3 rounded-md font-medium transition-colors ${
              saving
                ? 'bg-jet-gray-300 text-jet-gray-500 cursor-not-allowed'
                : 'bg-jet-yellow hover:bg-jet-yellow-hover text-jet-gray-900'
            }`}
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-jet-gray-600 mr-2"></div>
                Saving...
              </div>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TravelPreferencesPage;